import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { Dropdown, Input } from "../../../common/components/Input";
import { ButtonText } from "../../../common/components/Button";
import InfoIconWithTooltip from "../../../common/components/Icon/InfoIconWithTooltip";
import { saveLrsConfig, testLrsConnection } from "../state/lrsSlice";
import type {
  ILrsConfig,
  ILrsConfigPatch,
  ILrsConnectionTest,
  LrsActorMode,
} from "../state/lrsSlice";
import { lrsRequestErrorMessage, lrsTestMessage } from "../lrsMessages";

export const LRS_CONNECTION_DIALOG_COMPONENT_ID = "lrsConnectionDialog";

const FIELD_CLASS = "font-medium bg-white text-gray-600 text-sm w-full";

interface LrsConnectionDialogProps {
  isOpen: boolean;
  config: ILrsConfig | null;
  onClose: () => void;
  onSaved: (test: ILrsConnectionTest | null, enabled: boolean) => void;
}

type ConnectionValues = {
  endpoint: string;
  auth_key: string;
  auth_secret: string;
  xapi_version: string;
  actor_mode: LrsActorMode;
};

const FieldLabel = ({ text, hint }: { text: string; hint?: string }) => (
  <div className="flex items-center mb-1">
    <label className="block text-sm font-medium text-gray-700">{text}</label>
    {hint ? <InfoIconWithTooltip message={hint} className="ml-2" /> : null}
  </div>
);

const LrsConnectionDialog = ({ isOpen, config, onClose, onSaved }: LrsConnectionDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [testFailed, setTestFailed] = useState(false);

  const isSetup = !config || !config.endpoint || !config.has_credentials;
  const actorLocked = Boolean(config?.actor_mode_locked_at);

  const { register, handleSubmit, reset } = useForm<ConnectionValues>({
    defaultValues: {
      endpoint: "",
      auth_key: "",
      auth_secret: "",
      xapi_version: "1.0.3",
      actor_mode: "pseudonym",
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({
      endpoint: config?.endpoint ?? "",
      auth_key: "",
      auth_secret: "",
      xapi_version: config?.xapi_version ?? "1.0.3",
      actor_mode: config?.actor_mode ?? "pseudonym",
    });
    setFailure(null);
    setTestFailed(false);
    setBusy(false);
  }, [isOpen]);

  const validate = (values: ConnectionValues): string | null => {
    if (!values.endpoint.trim()) {
      return t("pages.profile.organization.lrs.errors.endpoint_required");
    }
    const hasKey = Boolean(values.auth_key);
    const hasSecret = Boolean(values.auth_secret);
    if (isSetup && (!hasKey || !hasSecret)) {
      return t("pages.profile.organization.lrs.errors.credentials_required");
    }
    if (hasKey !== hasSecret) {
      return t("pages.profile.organization.lrs.errors.credentials_incomplete");
    }
    return null;
  };

  const submit = (skipTest: boolean) =>
    handleSubmit(async (values) => {
      const invalid = validate(values);
      if (invalid) {
        setFailure(invalid);
        return;
      }
      setFailure(null);
      setBusy(true);
      const endpoint = values.endpoint.trim();
      const credentials =
        values.auth_key && values.auth_secret
          ? { auth_key: values.auth_key, auth_secret: values.auth_secret }
          : {};

      let test: ILrsConnectionTest | null = null;
      if (!skipTest) {
        const tested = await dispatch(
          testLrsConnection({ endpoint, ...credentials, xapi_version: values.xapi_version }),
        );
        if (!testLrsConnection.fulfilled.match(tested)) {
          setBusy(false);
          setFailure(lrsRequestErrorMessage(t, tested.payload));
          return;
        }
        test = tested.payload;
        if (!test.ok) {
          setBusy(false);
          setTestFailed(true);
          setFailure(lrsTestMessage(t, test));
          return;
        }
      }

      const patch: ILrsConfigPatch = { xapi_version: values.xapi_version, ...credentials };
      if (endpoint !== (config?.endpoint ?? "")) {
        patch.endpoint = endpoint;
      }
      if (isSetup) {
        patch.enabled = true;
        if (!actorLocked) {
          patch.actor_mode = values.actor_mode;
        }
      }
      const saved = await dispatch(
        saveLrsConfig({ componentId: LRS_CONNECTION_DIALOG_COMPONENT_ID, patch }),
      );
      setBusy(false);
      if (!saveLrsConfig.fulfilled.match(saved)) {
        setFailure(lrsRequestErrorMessage(t, saved.payload));
        return;
      }
      onSaved(test, isSetup);
    });

  return (
    <ModalStandard
      id="lrs-connection-dialog"
      fullScreen={false}
      controlledMode={{ isOpen }}
      onClose={onClose}
      showCloseButton
      minimize
      className="w-full max-w-xl h-fit"
    >
      <div className="completely-centered z-50">
        <IsLoading componentId={LRS_CONNECTION_DIALOG_COMPONENT_ID} showSuccess={false} />
      </div>
      <h2 className="pt-1 pb-1 font-medium text-lg pr-6">
        {t("pages.profile.organization.lrs.connectionDialogTitle")}
      </h2>
      <h3 className="pb-3 text-sm text-gray-500">
        {t("pages.profile.organization.lrs.connectionDialogSubtitle")}
      </h3>

      <form onSubmit={(event) => void submit(false)(event)} className="flex flex-col gap-3">
        <div>
          <FieldLabel
            text={t("pages.profile.organization.lrs.endpoint")}
            hint={t("pages.profile.organization.lrs.endpointHint")}
          />
          <Input
            type="text"
            autoFocus
            autoComplete="off"
            registerFct={() => register("endpoint")}
            className={FIELD_CLASS}
            placeholder={t("pages.profile.organization.lrs.endpointPlaceholder")}
          />
        </div>

        <div>
          <FieldLabel
            text={t("pages.profile.organization.lrs.credentials")}
            hint={t("pages.profile.organization.lrs.credentialsHint")}
          />
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="text"
              autoComplete="off"
              registerFct={() => register("auth_key")}
              className={FIELD_CLASS}
              placeholder={
                isSetup
                  ? t("pages.profile.organization.lrs.authKey")
                  : t("pages.profile.organization.lrs.keepKeyPlaceholder")
              }
            />
            <Input
              type="password"
              autoComplete="new-password"
              registerFct={() => register("auth_secret")}
              className={FIELD_CLASS}
              placeholder={
                isSetup
                  ? t("pages.profile.organization.lrs.authSecret")
                  : t("pages.profile.organization.lrs.keepSecretPlaceholder", {
                      last4: config?.secret_last4 ?? "",
                    })
              }
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="sm:w-1/2">
            <FieldLabel
              text={t("pages.profile.organization.lrs.xapiVersion")}
              hint={t("pages.profile.organization.lrs.xapiVersionHint")}
            />
            <Dropdown
              key={`xapi-version-${String(isOpen)}`}
              width="w-full"
              options={[
                { value: "1.0.3", optionText: "xAPI 1.0.3" },
                { value: "2.0.0", optionText: "xAPI 2.0.0" },
              ]}
              asDefaultValue
              defaultValueIndex={config?.xapi_version === "2.0.0" ? 1 : 0}
              registerFct={() => register("xapi_version")}
            />
          </div>
          {isSetup && !actorLocked ? (
            <div className="sm:w-1/2">
              <FieldLabel
                text={t("pages.profile.organization.lrs.actorMode")}
                hint={t("pages.profile.organization.lrs.actorModeHint")}
              />
              <Dropdown
                key={`actor-mode-${String(isOpen)}`}
                width="w-full"
                options={[
                  {
                    value: "pseudonym",
                    optionText: t("pages.profile.organization.lrs.actorPseudonym"),
                  },
                  { value: "email", optionText: t("pages.profile.organization.lrs.actorEmail") },
                ]}
                asDefaultValue
                defaultValueIndex={config?.actor_mode === "email" ? 1 : 0}
                registerFct={() => register("actor_mode")}
              />
            </div>
          ) : null}
        </div>

        <p
          role="alert"
          className={`min-h-[2.5rem] text-sm text-red-600 ${failure ? "" : "invisible"}`}
        >
          {failure ?? "-"}
        </p>

        <div className="flex justify-end items-center gap-3 pt-3 border-t border-gray-200">
          {testFailed ? (
            <ButtonText
              type="button"
              className="text-sm"
              disabled={busy}
              text={t("pages.profile.organization.lrs.saveAnyway")}
              onClick={(event) => void submit(true)(event)}
            />
          ) : null}
          <button type="button" className="btn-alternative-outline text-sm" onClick={onClose}>
            {t("general.cancel")}
          </button>
          <button type="submit" className="btn-primary-fill text-sm" disabled={busy}>
            {busy
              ? t("pages.profile.organization.lrs.testing")
              : isSetup
              ? t("pages.profile.organization.lrs.testAndEnable")
              : t("pages.profile.organization.lrs.testAndSave")}
          </button>
        </div>
      </form>
    </ModalStandard>
  );
};

export default LrsConnectionDialog;
